import { useMediaQuery } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getUserSettings, setSetting } from "../api/setting";
import { darkTheme, lightTheme } from "../lib/theme";
import { ActionType, AppDispatch, RootState } from "../reducer";
import { AddSettingsAction } from "../reducer/setting";
import { Setting, SettingUserData, SettingValue } from "../types/setting";
import { useUserEmail } from "./user";

export const useAddSettings: () => (settings: Setting[]) => void = () => {
  const dispatch = useDispatch<AppDispatch>();

  return (settings) => {
    const action: AddSettingsAction = {
      type: ActionType.ADD_SETTINGS,
      settings,
    };
    dispatch(action as any);
  };
};

export const useFetchUserSettings: () => () => Promise<any> = () => {
  const user = useUserEmail();
  const addSettings = useAddSettings();

  return async () => {
    if (!user) return;
    getUserSettings({ user }).then((res) => {
      addSettings(res.settings);
    });
  };
};

export const useSetSetting: () => (
  setting: SettingValue
) => Promise<any> = () => {
  const user = useUserEmail();
  const addSettings = useAddSettings();

  return async (setting) => {
    if (!user) return;
    const settingUser: SettingUserData = { ...setting, user };
    const settingFull: Setting = {
      ...settingUser,
      type: "omega/query-omega/setting/Setting",
    };
    addSettings([settingFull]);
    return setSetting(settingUser);
  };
};

export const useSetting = (key: string) => {
  const settingValue = useSettingValue(key);
  const setSetting = useSetSetting();
  const setSettingValue = (value: string) => setSetting({ key, value });
  return { settingValue, setSettingValue };
};

export const useSettingValue: (key: string) => string = (key) => {
  const value = useSelector(
    (state: RootState) => state.settings.settingsMap[key]?.value
  );
  return value;
};

export const useIsDarkMode = () => {
  const setSetting = useSetSetting();
  const isDarkModeSetting =
    useSettingValue("is-dark-mode") === "false" ? false : true;
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const theme = prefersDarkMode ? darkTheme : lightTheme;
  const isDarkMode = prefersDarkMode;

  const setDarkMode = (darkMode: boolean) =>
    setSetting({ key: "is-dark-mode", value: darkMode.toString() });

  return { isDarkMode, setDarkMode, theme };
};
